import {
  type ChargeName,
  type Currency,
  getCanadianTaxCharges,
  MEXICAN_TAX_CHARGES,
} from '@fv/models'
import { isTaxCharge, taxChargeNames } from '@fv/models/core'

import { type ChargeModel, chargeOptions, generateCharge } from './charges.js'

export type AppliedTax = { name: ChargeName; amount: number }

type TaxableModel = {
  currency: Currency
  amount?: number
  charges?: ChargeModel[]
}

export type LocationLike = { type?: string; country?: string; state?: string }

const round2 = (n: number) => Math.round(n * 100) / 100

export function getDestinationCanadianProvince(
  locations: LocationLike[] | undefined,
): string | null {
  const origin = locations?.find(l => l.type === 'origin')
  const dest = locations?.find(l => l.type === 'destination')
  if (origin?.country?.toLowerCase() !== 'ca') return null
  if (dest?.country?.toLowerCase() !== 'ca' || !dest.state) return null
  return dest.state
}

function getOriginCanadianProvince(
  locations: LocationLike[] | undefined,
): string | null {
  const origin = locations?.find(l => l.type === 'origin')
  if (origin?.country?.toLowerCase() !== 'ca' || !origin.state) return null
  return origin.state
}

export function isMexicoIntra(locations: LocationLike[] | undefined): boolean {
  const origin = locations?.find(l => l.type === 'origin')
  const destination = locations?.find(l => l.type === 'destination')
  return (
    origin?.country?.toLowerCase() === 'mx' &&
    destination?.country?.toLowerCase() === 'mx'
  )
}

export function computeTaxCharges(
  currency: Currency,
  baseAmount: number,
  locations: LocationLike[] | undefined,
): AppliedTax[] {
  if (!baseAmount) return []

  if (currency === 'cad') {
    const province = getDestinationCanadianProvince(locations)
    const originProvince = getOriginCanadianProvince(locations)
    if (!province || !originProvince) return []
    return getCanadianTaxCharges(originProvince, province).map(t => ({
      name: t.name,
      amount: round2(baseAmount * t.rate),
    }))
  }

  if (currency === 'mxn' && isMexicoIntra(locations)) {
    return MEXICAN_TAX_CHARGES.map(t => ({
      name: t.name,
      amount: round2(baseAmount * t.rate),
    }))
  }

  return []
}

// True for any tax charge the system applies automatically based on
// currency + lane (so the user shouldn't be able to add it manually).
// CAD taxes are always system-managed; MXN's IVA is only managed for
// intra-Mexico shipments — outside that lane, IVA may be added by hand.
export function isManagedTax(
  name: ChargeName,
  currency: Currency | undefined,
  locations: LocationLike[] | undefined,
): boolean {
  if (!isTaxCharge(name)) return false
  if (currency === 'mxn') return name === 'iva' && isMexicoIntra(locations)
  return true
}

export function canManuallyAddIva(
  currency: Currency | undefined,
  locations: LocationLike[] | undefined,
): boolean {
  return currency === 'mxn' && !isMexicoIntra(locations)
}

const autoManagedTaxNames = taxChargeNames.filter(n => n !== 'iva')

export function getManualChargeOptions(
  currency: Currency | undefined,
  locations: LocationLike[] | undefined,
): typeof chargeOptions {
  const allowIva = canManuallyAddIva(currency, locations)
  return chargeOptions.filter(o => {
    if (o.value === 'freightview markup') return false
    if (o.value === 'iva') return allowIva
    if (autoManagedTaxNames.some(n => n === o.value)) return false
    return true
  })
}

export function getApplicableTaxes(
  currency: Currency,
  baseAmount: number,
  locations: LocationLike[] | undefined,
): { taxes: AppliedTax[]; total: number } | null {
  const taxes = computeTaxCharges(currency, baseAmount, locations)
  if (!taxes.length) return null
  return {
    taxes,
    total: taxes.reduce((sum, t) => sum + t.amount, baseAmount),
  }
}

export function applyTaxes<T extends TaxableModel>(
  model: T,
  locations: LocationLike[] | undefined,
): T {
  const isManaged = (name: ChargeName) =>
    isManagedTax(name, model.currency, locations)
  const hasCharges = (model.charges?.length ?? 0) > 0
  const baseAmount = hasCharges
    ? (model.charges
        ?.filter(c => !isManaged(c.name))
        .reduce((acc, c) => acc + (c.amount || 0), 0) ?? 0)
    : (model.amount ?? 0)

  const taxes = computeTaxCharges(model.currency, baseAmount, locations)

  if (!taxes.length) {
    if (!model.charges?.some(c => isManaged(c.name))) return model
    return {
      ...model,
      charges: model.charges.filter(c => !isManaged(c.name)),
    }
  }

  const existingNonTax = hasCharges
    ? (model.charges?.filter(c => !isManaged(c.name)) ?? [])
    : [generateCharge({ name: 'linehaul', amount: baseAmount })]
  const existingTaxByName = new Map(
    model.charges?.filter(c => isManaged(c.name)).map(c => [c.name, c]) ?? [],
  )
  const taxCharges = taxes.map(t => {
    const existing = existingTaxByName.get(t.name)
    return existing ? { ...existing, amount: t.amount } : generateCharge(t)
  })
  return { ...model, charges: [...existingNonTax, ...taxCharges] }
}
